import React, { useState, useContext, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PublishIcon from "@material-ui/icons/Publish";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import { WhatsApp } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { parseCrmTemplateFile, validateCrmTemplateRow } from "./crmTemplateXlsx";

const useStyles = makeStyles(theme => ({
  dialogPaper: { borderRadius: 12 },
  warningBox: {
    display: "flex", alignItems: "flex-start", gap: 8,
    background: "#fff3e0", border: "1px solid #ffb74d",
    borderRadius: 8, padding: "10px 12px", marginBottom: 16, fontSize: "0.82rem", color: "#5d4037",
  },
  dropZone: {
    border: "2px dashed #bbb", borderRadius: 8, padding: 24, textAlign: "center",
    cursor: "pointer", marginBottom: 16, background: "#fafafa",
  },
  fileName: { fontWeight: 600, marginBottom: 4 },
  summaryBox: {
    display: "flex", gap: 16, alignItems: "center",
    padding: "8px 12px", borderRadius: 8, background: "#f5f5f5", fontSize: "0.8rem", marginTop: 16,
  },
  resultList: { maxHeight: 220, overflowY: "auto", marginTop: 12 },
  resultRow: { display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: "0.8rem" },
}));

// dd/MM/yyyy ou yyyy-MM-dd (o Excel pode reformatar a célula de data
// dependendo de como o usuário editou), devolve yyyy-MM-dd ou null.
const normalizeDate = (value) => {
  if (!value) return null;
  const v = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const br = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return null;
};

const CrmImportTemplateModal = ({ open, onClose, tags = [], onImported }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId: userWhatsappId } = user;
  const fileInputRef = useRef(null);

  const [fileName, setFileName] = useState("");
  // Linhas lidas do arquivo, cada uma já carregando sua validação local:
  // { ...campos, validationErrors: string[] }
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const queues = user.queues || [];
  const [whatsapps, setWhatsapps] = useState([]);
  const [defaultQueue, setDefaultQueue] = useState(user.queues?.length === 1 ? user.queues[0].id : "");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(userWhatsappId || "");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const laneNames = new Set(tags.map(t => t.name.trim().toLowerCase()));
  const queueNames = new Set(queues.map(q => q.name.trim().toLowerCase()));

  useEffect(() => {
    if (!open) return;
    setFileName("");
    setRows([]);
    setParseError("");
    setSubmitting(false);
    setResults(null);
    api.get("/whatsapp", { params: { companyId, session: 0 } })
      .then(({ data }) => setWhatsapps(data || []))
      .catch(err => toastError(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResults(null);
    setParseError("");
    try {
      const parsedRows = await parseCrmTemplateFile(file);
      if (parsedRows.length === 0) {
        setParseError("Nenhuma linha com dados foi encontrada no arquivo.");
        setRows([]);
        return;
      }
      // Validação client-side por linha, para o usuário ver o problema
      // antes mesmo de enviar ao servidor (que valida de novo, por segurança).
      const validated = parsedRows.map(r => ({
        ...r,
        validationErrors: validateCrmTemplateRow(r, { laneNames, queueNames }),
      }));
      setRows(validated);
    } catch (err) {
      setParseError(err.message || "Não foi possível ler o arquivo.");
      setRows([]);
    }
  };

  const validRows = rows.filter(r => r.validationErrors.length === 0);
  const invalidRows = rows.filter(r => r.validationErrors.length > 0);

  const buildPayloadRows = () => validRows.map(r => ({
    name: r.name,
    number: r.number,
    email: r.email,
    carteira: r.carteira,
    observacoes: r.observacoes,
    laneTagId: r.laneName || null,
    crmStatus: r.crmStatus || null,
    valorVenda: r.valorVenda ? r.valorVenda.replace(",", ".") : null,
    dataInicio: normalizeDate(r.dataInicio),
    dataFim: normalizeDate(r.dataFim),
    queueName: r.queueName || null,
  }));

  const handleSubmit = async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/tickets/import-crm-template", {
        rows: buildPayloadRows(),
        defaultQueueId: defaultQueue || null,
        whatsappId: selectedWhatsapp || null,
      });
      setResults(data.results);

      const created = data.results.filter(r => r.status === "created").length;
      const updated = data.results.filter(r => r.status === "updated").length;
      const errors = data.results.filter(r => r.status === "error").length;

      if (created > 0 || updated > 0) {
        toast.success(`${created} card(s) criado(s), ${updated} atualizado(s)!`);
        onImported && onImported();
      }
      if (errors > 0) {
        toast.warn(`${errors} linha(s) com erro. Veja o detalhe abaixo.`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const connectedWhatsapps = whatsapps.filter(w => w.status === "CONNECTED");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: classes.dialogPaper }}>
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Importar Cards em Massa</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.warningBox}>
          <ErrorIcon style={{ color: "#e65100", fontSize: 20, marginTop: 1 }} />
          <span>
            Envie o arquivo exatamente como foi exportado pela opção "Exportar Template". Se as
            colunas com 🔒 (Nome, Número, País, E-mail, Carteira, Observações) forem alteradas,
            a linha correspondente será rejeitada na importação.
          </span>
        </div>

        <input
          type="file"
          accept=".xlsx,.xls"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <div className={classes.dropZone} onClick={() => fileInputRef.current?.click()}>
          <PublishIcon style={{ fontSize: 32, color: "#999" }} />
          <Typography>{fileName || "Clique para selecionar o arquivo .xlsx preenchido"}</Typography>
        </div>

        {parseError && (
          <Typography style={{ color: "#f44336", marginBottom: 12 }}>{parseError}</Typography>
        )}

        {rows.length > 0 && !results && (
          <Typography style={{ marginBottom: 16 }}>
            {rows.length} linha(s) lida(s) do arquivo
            {invalidRows.length > 0 ? ` — ${invalidRows.length} com problema (não serão enviadas)` : ""}.
          </Typography>
        )}

        <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
          <InputLabel>Setor padrão (linhas sem Setor preenchido)</InputLabel>
          <Select value={defaultQueue} onChange={e => setDefaultQueue(e.target.value)} label="Setor padrão (linhas sem Setor preenchido)">
            <MenuItem value=""><em>Nenhum</em></MenuItem>
            {queues.map(q => (
              <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {connectedWhatsapps.length > 1 && (
          <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
            <InputLabel>Conexão WhatsApp (cards novos)</InputLabel>
            <Select value={selectedWhatsapp} onChange={e => setSelectedWhatsapp(e.target.value)} label="Conexão WhatsApp (cards novos)">
              {connectedWhatsapps.map(w => (
                <MenuItem key={w.id} value={w.id}>
                  <WhatsApp style={{ fontSize: 14, color: "#25d366", marginRight: 6, verticalAlign: "middle" }} />
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {invalidRows.length > 0 && !results && (
          <div className={classes.resultList}>
            {invalidRows.map((r, idx) => (
              <div key={`${r.number}-${idx}`} className={classes.resultRow}>
                <ErrorIcon style={{ color: "#f44336", fontSize: 16 }} />
                <Tooltip title={r.validationErrors.join("; ")}>
                  <span>{r.name || "(sem nome)"} ({r.number || "sem número"}) — {r.validationErrors.join("; ")}</span>
                </Tooltip>
              </div>
            ))}
          </div>
        )}

        {results && (
          <>
            <div className={classes.summaryBox}>
              <span style={{ color: "#4caf50", fontWeight: 700 }}>
                {results.filter(r => r.status === "created").length} criados
              </span>
              <span style={{ color: "#2196f3", fontWeight: 700 }}>
                {results.filter(r => r.status === "updated").length} atualizados
              </span>
              <span style={{ color: "#f44336", fontWeight: 700 }}>
                {results.filter(r => r.status === "error").length} com erro
              </span>
            </div>
            <div className={classes.resultList}>
              {results.filter(r => r.status === "error").map((r, idx) => (
                <div key={`${r.number}-${idx}`} className={classes.resultRow}>
                  <ErrorIcon style={{ color: "#f44336", fontSize: 16 }} />
                  <Tooltip title={r.reason || ""}>
                    <span>{r.number} — {r.reason}</span>
                  </Tooltip>
                </div>
              ))}
              {results.filter(r => r.status !== "error").map((r, idx) => (
                <div key={`${r.number}-${idx}`} className={classes.resultRow}>
                  <CheckCircleIcon style={{ color: "#4caf50", fontSize: 16 }} />
                  <span>{r.number} — {r.status === "created" ? "card criado" : "card atualizado"}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px" }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8 }}>
          {results ? "Fechar" : "Cancelar"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || validRows.length === 0 || !!results}
          style={{ borderRadius: 8 }}
        >
          {submitting ? <CircularProgress size={18} style={{ marginRight: 8 }} /> : null}
          Importar {validRows.length > 0 ? `(${validRows.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrmImportTemplateModal;
